import {
    createAsyncThunk, createSlice, createSelector, PayloadAction, createDraftSafeSelector
 } from '@reduxjs/toolkit';
import { RootState } from './store';
import { Concept } from "./ConceptSlice";
import http from '../utils/http';
import { TaxonomyTypeEnum, VersionRef } from './TaxonomyVersionSlice';


export interface ConceptNode {
    networkId: string;
    concept: Concept;
    children: Array<ConceptNode>;
    referenceTypes: Array<string> | null;
}

export interface RestrictedAccountsFilter {
    entityCode: string;
    taxonomyVersion: VersionRef;
}

export interface NetworkConceptsFilter {
    nameFilter: string;
    taxonomyVersion: VersionRef;
}

export interface AccountRestrictionPayload {
    resultTree: Array<ConceptNode>;
    filter: RestrictedAccountsFilter;
}

export interface NetworkConceptsPayload {
    resultTree: Array<ConceptNode>;
    filter: NetworkConceptsFilter;
}

export interface AccountsState {
    restrictedResults: Array<ConceptNode>;
    filteredResults: Array<ConceptNode>;
    filter: RestrictedAccountsFilter | null;
    networkFilter: NetworkConceptsFilter | null;
    axesForSelectedConcept: Array<ConceptNode>;
    status: 'idle' | 'loading' | 'failed';
}

const initialState: AccountsState = {
    restrictedResults: [],
    filteredResults: [],
    filter: null,
    networkFilter: null,
    axesForSelectedConcept: [],
    status: "idle"
}

const emptyPayload: AccountRestrictionPayload = {
    resultTree: [],
    filter: {
        entityCode: "",
        taxonomyVersion: { type: TaxonomyTypeEnum.AFR, year: 2019 }
    }
}

export const emptyNCPayload: NetworkConceptsPayload = {
    resultTree: [],
    filter: {
        nameFilter: "",
        taxonomyVersion: { type: TaxonomyTypeEnum.AFR, year: 2019 }
    }
}

// Return a map of accounts by code
export const populateAccountCodeMap = function (nodeList: ConceptNode[], codeMap: any): any {
    // the == tests for null and undefined
    if (nodeList == null ) {
        return;
    }
    for (var n of nodeList) {
        if (n.concept && n.concept.code) {
            codeMap[n.concept.code] = n.concept.name;
        }
        populateAccountCodeMap(n.children, codeMap);
    }
}

export const loadRestrictedAccounts = createAsyncThunk(
    "Taxonomy/GetRestrictedAccounts",
    async (restrictedAccountsFilter: RestrictedAccountsFilter): Promise<AccountRestrictionPayload> => {
        try {
            const result = await http<RestrictedAccountsFilter, AccountRestrictionPayload>({
                path: `/taxonomy/getRestrictedAccounts`,
                method: 'post',
                body: restrictedAccountsFilter
            });
            if (result.ok && result.parsedBody) {
                return result.parsedBody;
            }
            else {
                return emptyPayload;
            }
        } catch (ex) {
            return emptyPayload;
        }
    });
export const loadAllAccounts = createAsyncThunk(
    "Taxonomy/GetAllAccounts",
    async (ncFilter: NetworkConceptsFilter): Promise<NetworkConceptsPayload> => {
        try {
            const result = await http<NetworkConceptsFilter, NetworkConceptsPayload>({
                path: `/taxonomy/getAllAccounts`,
                method: 'post',
                body: ncFilter
            });
            if (result.ok && result.parsedBody) {
                return result.parsedBody;
            }
            else {
                return emptyNCPayload;
            }
        } catch (ex) {
            return emptyNCPayload;
        }
    });
export const loadAxesForConcept = createAsyncThunk(
    "Taxonomy/AxesForConcept",
    async (ncFilter: NetworkConceptsFilter): Promise<Array<ConceptNode>> => {
        try {
            var entryPoint = ncFilter.taxonomyVersion.year + "";
            var conceptId = ncFilter.nameFilter;
            const result = await http<undefined, Array<ConceptNode>>({
                path: `/taxonomy/${ncFilter.taxonomyVersion.type}/${entryPoint}/axesForConcept/${conceptId}`,
            });
            if (result.ok && result.parsedBody) {
                return result.parsedBody;
            }
            else {
                return [];
            }
        } catch (ex) {
            return [];
        }
    });
export const accountsSlice = createSlice({
    name: 'Accounts',
    initialState,
    reducers: {
        resetAccountsState: (state: any) => {
            return initialState
        },
        clearAxesForConcepts: {
            reducer(state: any) {
                state.axesForConcepts = [];
            }
        }
    },
    extraReducers: (builder: any) => {
        builder
            .addCase(loadRestrictedAccounts.pending, (state: any) => {
                state.status = "loading";
            })
            .addCase(loadRestrictedAccounts.fulfilled, (state: any, action: any) => {
                state.status = "idle";
                state.restrictedResults = action.payload.resultTree;
                state.filter = action.payload.filter;
            })
            .addCase(loadAllAccounts.pending, (state: any) => {
                state.status = "loading";
            })
            .addCase(loadAllAccounts.fulfilled, (state: any, action: any) => {
                state.status = "idle";
                state.filteredResults = action.payload.resultTree;
                state.networkFilter = action.payload.filter;
            })
            .addCase(loadAxesForConcept.pending, (state: any) => {
                state.status = "loading";
            })
            .addCase(loadAxesForConcept.fulfilled, (state: any, action: any) => {
                state.status = "idle";
                state.axesForConcept = action.payload;
            })
    }
});

export const { resetAccountsState, clearAxesForConcepts } = accountsSlice.actions;

export const selectAccountTree = (state: RootState) => state.accounts ? state.accounts.restrictedResults : null;

export const selectFilteredAccountTree = (state: RootState) => state.accounts ? state.accounts.filteredResults : null;

export const selectAccountFilter = (state: RootState) => state.accounts ? state.accounts.filter : null;

export const selectAxesForConcept = (state: RootState) => state.accounts ? state.accounts.axesForConcept : [];

export default accountsSlice.reducer;
